import React, { createContext, useContext, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import { apiRequest } from './api';
import MarketingPage from './pages/MarketingPage';
import LoginPage from './pages/LoginPage';
import AppShell from './pages/AppShell';
import DashboardPage from './pages/DashboardPage';
import AccountsPage from './pages/AccountsPage';
import RecommendationsPage from './pages/RecommendationsPage';
import UsersPage from './pages/UsersPage';
import SecurityPage from './pages/SecurityPage';
import AssistantPage from './pages/AssistantPage';
import WebsiteInboxPage from './pages/WebsiteInboxPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import LiabilityDisclaimerPage from './pages/LiabilityDisclaimerPage';
import DocsPage from './pages/DocsPage';

const tokenStorageKey = 'sroik.access_token';

const emptyRegisterForm = { tenant_name: '', plan: 'starter', email: '', password: '', full_name: '' };
const emptyLoginForm = { email: '', password: '', otp_code: '', authenticator_code: '', recovery_code: '' };
const emptyLoginInviteForm = { token: '', otp_code: '', full_name: '', password: '' };
const emptyResetRequestForm = { email: '' };
const emptyResetConfirmForm = { token: '', new_password: '' };
const emptyOtpForm = { otp_code: '' };

export const AppContext = createContext(null);
export function useApp() { return useContext(AppContext); }

function ProtectedRoute({ children }) {
  const { authToken, currentUser, authReady } = useApp();
  if (!authToken) return <Navigate to="/login" replace />;
  if (!authReady) {
    return (
      <div className="page-view">
        <div className="empty-state">
          <p className="feature-eyebrow">Authenticating</p>
          <p>Validating your session before loading workspace data...</p>
        </div>
      </div>
    );
  }
  return currentUser ? children : <Navigate to="/login" replace />;
}

function PublicOnlyRoute({ children }) {
  const { authToken, currentUser, authReady } = useApp();
  if (!authToken) return children;
  if (!authReady) return null;
  return currentUser ? <Navigate to="/app/accounts" replace /> : children;
}

function AppWithRouter() {
  const navigate = useNavigate();
  const location = useLocation();

  function isAuthTokenError(error) {
    const message = (error?.message || '').toLowerCase();
    return (
      message === 'invalid token'
      || message.includes('not authenticated')
      || message.includes('could not validate credentials')
      || message.includes('token has expired')
      || message.includes('signature has expired')
    );
  }

  const [authToken, setAuthToken] = useState(() => localStorage.getItem(tokenStorageKey) || '');
  const [authReady, setAuthReady] = useState(() => !localStorage.getItem(tokenStorageKey));
  const [authMode, setAuthMode] = useState('login');
  const [loginForm, setLoginForm] = useState(emptyLoginForm);
  const [registerForm, setRegisterForm] = useState(emptyRegisterForm);
  const [resetRequestForm, setResetRequestForm] = useState(emptyResetRequestForm);
  const [resetConfirmForm, setResetConfirmForm] = useState(emptyResetConfirmForm);
  const [loginInviteForm, setLoginInviteForm] = useState(emptyLoginInviteForm);
  const [health, setHealth] = useState({ state: 'loading', message: 'Checking...' });
  const [catalog, setCatalog] = useState({ plans: [], frameworks: [], security_capabilities: [] });
  const [catalogState, setCatalogState] = useState('loading');
  const [currentUser, setCurrentUser] = useState(null);
  const [currentTenant, setCurrentTenant] = useState(null);
  const [securityPosture, setSecurityPosture] = useState(null);
  const [authMessage, setAuthMessage] = useState('');
  const [authError, setAuthError] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  const [mfaSetup, setMfaSetup] = useState(null);
  const [mfaEnableCode, setMfaEnableCode] = useState('');
  const [mfaDisableForm, setMfaDisableForm] = useState({ authenticator_code: '', recovery_code: '' });
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [dashboardPublic, setDashboardPublic] = useState(null);
  const [dashboardOverview, setDashboardOverview] = useState(null);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [weeklySummaryWeek, setWeeklySummaryWeek] = useState('');
  const [agingQueue, setAgingQueue] = useState(null);
  const [budgetRisk, setBudgetRisk] = useState(null);
  const [allocationConfidence, setAllocationConfidence] = useState(null);
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditError, setAuditError] = useState('');
  const [oidcProvider, setOidcProvider] = useState(null);
  const [oidcCode, setOidcCode] = useState('');
  const [cloudAccounts, setCloudAccounts] = useState([]);
  const [cloudAccountRoleById, setCloudAccountRoleById] = useState({});
  const [recommendations, setRecommendations] = useState([]);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [cloudAccountForm, setCloudAccountForm] = useState({ provider: 'aws', account_id: '', role_arn: '', external_id: '' });
  const [cloudBootstrap, setCloudBootstrap] = useState(null);
  const [cloudBootstrapBusy, setCloudBootstrapBusy] = useState(false);
  const [cloudBootstrapError, setCloudBootstrapError] = useState('');
  const [cloudSyncBusy, setCloudSyncBusy] = useState(false);
  const [cloudSyncAllBusy, setCloudSyncAllBusy] = useState(false);
  const [cloudSyncByAccount, setCloudSyncByAccount] = useState({});
  const [cloudSyncLastByAccount, setCloudSyncLastByAccount] = useState({});
  const [cloudSyncMessage, setCloudSyncMessage] = useState('');
  const [cloudSyncError, setCloudSyncError] = useState('');
  // active background sync jobs: { [jobId]: { scope: 'all'|accountId, startedAt } }
  const [activeSyncJobs, setActiveSyncJobs] = useState({});
  const [showRecoveryLoginInput, setShowRecoveryLoginInput] = useState(false);
  const [mfaChallengeRequired, setMfaChallengeRequired] = useState(false);
  const [otpChallengeRequired, setOtpChallengeRequired] = useState(false);
  const [otpForm, setOtpForm] = useState(emptyOtpForm);
  const [otpDebugCode, setOtpDebugCode] = useState('');
  const [loginOtpDebugCode, setLoginOtpDebugCode] = useState('');

  useEffect(() => {
    let active = true;

    async function loadHealth() {
      try {
        const data = await apiRequest('/api/v1/health');
        if (!active) return;
        setHealth({ state: 'healthy', message: `${data.status} in ${data.environment}` });
      } catch {
        if (!active) return;
        setHealth({ state: 'degraded', message: 'API unreachable' });
      }
    }

    async function loadCatalog() {
      try {
        const data = await apiRequest('/api/v1/auth/plans');
        if (!active) return;
        setCatalog(data);
        setCatalogState('ready');
      } catch {
        if (!active) return;
        setCatalogState('error');
      }
    }

    async function loadPublicDashboard() {
      try {
        const data = await apiRequest('/api/v1/dashboard/public-overview');
        if (!active) return;
        setDashboardPublic(data);
      } catch {
        if (!active) return;
        setDashboardPublic(null);
      }
    }

    async function loadOidcProvider() {
      try {
        const data = await apiRequest('/api/v1/auth/oidc/provider');
        if (!active) return;
        setOidcProvider(data);
      } catch {
        if (!active) return;
        setOidcProvider({ enabled: false, message: 'OIDC configuration lookup failed.' });
      }
    }

    loadHealth();
    loadCatalog();
    loadPublicDashboard();
    loadOidcProvider();
    return () => { active = false; };
  }, []);

  function normalizeWeekValue(value) {
    const raw = (value || '').trim();
    if (!raw) return '';
    const match = raw.match(/^(\d{4})-W?(\d{1,2})$/i);
    if (!match) return '';
    return `${match[1]}-W${String(match[2]).padStart(2, '0')}`;
  }

  function weeklySummaryPath(weekValue) {
    const normalized = normalizeWeekValue(weekValue);
    if (!normalized) return '/api/v1/dashboard/weekly-summary';
    return `/api/v1/dashboard/weekly-summary?week=${encodeURIComponent(normalized)}`;
  }

  async function loadWeeklySummary(weekValue = '') {
    if (!authToken) return null;
    const data = await apiRequest(weeklySummaryPath(weekValue), {}, authToken);
    setWeeklySummary(data);
    setWeeklySummaryWeek(data?.week || normalizeWeekValue(weekValue) || '');
    return data;
  }

  useEffect(() => {
    if (!authToken) {
      setCurrentUser(null);
      setCurrentTenant(null);
      setSecurityPosture(null);
      setWeeklySummary(null);
      setWeeklySummaryWeek('');
      setAgingQueue(null);
      setBudgetRisk(null);
      setAllocationConfidence(null);
      localStorage.removeItem(tokenStorageKey);
      setAuthReady(true);
      return;
    }

    localStorage.setItem(tokenStorageKey, authToken);
    setAuthReady(false);
    let active = true;

    async function loadWorkspace() {
      try {
        const [user, posture] = await Promise.all([
          apiRequest('/api/v1/auth/me', {}, authToken),
          apiRequest('/api/v1/auth/security-posture', {}, authToken),
        ]);
        const weeklySummaryUrl = weeklySummaryWeek
          ? `/api/v1/dashboard/weekly-summary?week=${encodeURIComponent(weeklySummaryWeek)}`
          : '/api/v1/dashboard/weekly-summary';
        const [overview, summary, aging, risk, allocation, accounts, accountAccess, recs, users] = await Promise.all([
          apiRequest('/api/v1/dashboard/overview', {}, authToken),
          apiRequest(weeklySummaryUrl, {}, authToken),
          apiRequest('/api/v1/dashboard/aging-queue', {}, authToken),
          apiRequest('/api/v1/finops/budget-risk', {}, authToken),
          apiRequest('/api/v1/finops/allocation-confidence', {}, authToken),
          apiRequest('/api/v1/cloud-accounts', {}, authToken),
          apiRequest('/api/v1/cloud-accounts/my-access', {}, authToken),
          apiRequest('/api/v1/recommendations', {}, authToken),
          apiRequest('/api/v1/auth/users', {}, authToken),
        ]);
        if (!active) return;
        setCurrentUser(user);
        if (user.tenant_name) {
          setCurrentTenant({ name: user.tenant_name, plan: user.tenant_plan });
        }
        setSecurityPosture(posture);
        setDashboardOverview(overview);
        setWeeklySummary(summary);
        setWeeklySummaryWeek(summary?.week || '');
        setAgingQueue(aging);
        setBudgetRisk(risk);
        setAllocationConfidence(allocation);
        setCloudAccounts(accounts || []);
        setRecommendations(recs || []);
        setTenantUsers(users || []);
        setCloudAccountRoleById(
          (accountAccess || []).reduce((acc, item) => {
            acc[item.cloud_account_id] = item.role;
            return acc;
          }, {})
        );

        const hasWorkspaceData = (accounts || []).length > 0 || (recs || []).length > 0;
        if (location.pathname === '/app/dashboard' && !hasWorkspaceData) {
          navigate('/app/accounts', { replace: true });
        }

        try {
          const isPrivileged = ['owner', 'admin', 'security_auditor'].includes(user.role);
          const audit = isPrivileged
            ? await apiRequest('/api/v1/audit/events?limit=12', {}, authToken)
            : await apiRequest('/api/v1/audit/my-events?limit=12', {}, authToken);
          if (active) {
            setAuditEvents(isPrivileged ? (audit.items || []) : (audit || []));
            setAuditError('');
          }
        } catch {
          if (active) {
            setAuditEvents([]);
            setAuditError('Audit log API unavailable for this session.');
          }
        }
      } catch (error) {
        if (!active) return;
        if (isAuthTokenError(error)) {
          setAuthToken('');
          setAuthError('Session expired. Please sign in again.');
          setAuditEvents([]);
          setAuditError('');
          setCloudAccounts([]);
          setRecommendations([]);
          setTenantUsers([]);
          setCloudAccountRoleById({});
          setWeeklySummary(null);
          setWeeklySummaryWeek('');
          setAgingQueue(null);
          setBudgetRisk(null);
          setAllocationConfidence(null);
          return;
        }

        setAuthError('Workspace data refresh failed. Please retry.');
      } finally {
        if (active) {
          setAuthReady(true);
        }
      }
    }

    loadWorkspace();
    return () => { active = false; };
  }, [authToken, location.pathname, navigate, weeklySummaryWeek]);

  // Poll active background sync jobs every 3 seconds
  useEffect(() => {
    const jobIds = Object.keys(activeSyncJobs);
    if (!authToken || jobIds.length === 0) return undefined;

    const intervalId = setInterval(async () => {
      for (const jobId of jobIds) {
        try {
          const job = await apiRequest(`/api/v1/cloud-accounts/sync-jobs/${jobId}`, {}, authToken);
          if (job.status === 'complete') {
            const r = job.result;
            const issues = (r?.summaries || []).flatMap((s) => s.issues || []);
            const issueNote = issues.length > 0 ? ` Issues: ${issues.join(', ')}` : '';
            setCloudSyncMessage(
              `Sync complete: scanned ${r?.total_resources_scanned || 0} resources, created ${r?.total_recommendations_created || 0}, updated ${r?.total_recommendations_updated || 0}.${issueNote}`
            );
            if (r?.summaries) {
              setCloudSyncLastByAccount((prev) => {
                const next = { ...prev };
                r.summaries.forEach((s) => {
                  if (s.cloud_account_id) {
                    next[s.cloud_account_id] = {
                      synced_at: r.synced_at || null,
                      issues: s.issues || [],
                      resources_scanned: s.resources_scanned || 0,
                      recommendations_created: s.recommendations_created || 0,
                      recommendations_updated: s.recommendations_updated || 0,
                    };
                  }
                });
                return next;
              });
            }
            await refreshWorkspaceAnalytics();
            setActiveSyncJobs((prev) => {
              const next = { ...prev };
              delete next[jobId];
              return next;
            });
            const jobScope = activeSyncJobs[jobId]?.scope;
            if (jobScope && jobScope !== 'all') {
              setCloudSyncByAccount((prev) => { const n = { ...prev }; delete n[jobScope]; return n; });
            } else {
              setCloudSyncAllBusy(false);
              setCloudSyncByAccount({});
            }
          } else if (job.status === 'failed') {
            setCloudSyncError(job.error || 'Sync failed.');
            setActiveSyncJobs((prev) => {
              const next = { ...prev };
              delete next[jobId];
              return next;
            });
            const jobScope = activeSyncJobs[jobId]?.scope;
            if (jobScope && jobScope !== 'all') {
              setCloudSyncByAccount((prev) => { const n = { ...prev }; delete n[jobScope]; return n; });
            } else {
              setCloudSyncAllBusy(false);
              setCloudSyncByAccount({});
            }
          }
        } catch {
          // ignore transient poll errors
        }
      }
    }, 3000);

    return () => clearInterval(intervalId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSyncJobs, authToken]);

  const selectedPlan = catalog.plans.find((plan) => plan.key === registerForm.plan) || null;
  const dashboardData = dashboardOverview || dashboardPublic || {};
  const trendPoints = (dashboardData.monthly_delta_trend || []).filter((item) => Number(item.value || 0) !== 0);
  const riskItems = (dashboardData.risk_distribution || []).filter((item) => Number(item.count || 0) > 0);
  const categoryItems = (dashboardData.category_breakdown || []).filter((item) => Number(item.count || 0) > 0);
  const hasAnyAnalytics = trendPoints.length > 0 || riskItems.length > 0 || categoryItems.length > 0;
  const hasPublicData =
    (dashboardPublic?.kpis?.total_recommendations || 0) > 0 ||
    (dashboardPublic?.kpis?.potential_monthly_savings_usd || 0) > 0;
  const activeKpi = dashboardOverview || dashboardPublic;
  const kpiSavings = Number(activeKpi?.kpis?.potential_monthly_savings_usd || 0);
  const kpiImplemented = Number(activeKpi?.kpis?.implemented_monthly_savings_usd || 0);
  const kpiOpen = Number(activeKpi?.kpis?.open_recommendations || 0);
  const kpiAccepted = Number(activeKpi?.kpis?.accepted_recommendations || 0);
  const kpiSpend = Number(activeKpi?.kpis?.total_monthly_spend_usd || 0);
  const accountBreakdown = dashboardData.account_breakdown || [];
  const topActions = dashboardData.top_actions || [];

  function resetMessages() {
    setAuthError('');
    setAuthMessage('');
  }

  function handleInvalidTokenError(error) {
    if (!isAuthTokenError(error)) {
      return false;
    }
    setAuthToken('');
    setAuthError('Session expired. Please sign in again.');
    setCloudSyncAllBusy(false);
    setCloudSyncByAccount({});
    setActiveSyncJobs({});
    navigate('/login');
    return true;
  }

  function applySession(session) {
    if (session.access_token) setAuthToken(session.access_token);
    setCurrentUser(session.user || null);
    setCurrentTenant(session.tenant || null);
    setRecoveryCodes([]);
    if (session.mfa_setup) {
      setMfaSetup(session.mfa_setup);
    } else {
      setMfaSetup(null);
    }
  }

  async function handleRegister(event) {
    event.preventDefault();
    resetMessages();
    setAuthBusy(true);
    try {
      const response = await apiRequest('/api/v1/auth/register/send-otp', {
        method: 'POST',
        body: JSON.stringify({ email: registerForm.email }),
      });
      setOtpForm(emptyOtpForm);
      setOtpDebugCode(response.otp_code || '');
      setAuthMode('register-otp');
      setAuthMessage(response.message);
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleCompleteRegister(event) {
    event.preventDefault();
    resetMessages();
    setAuthBusy(true);
    try {
      const session = await apiRequest('/api/v1/auth/register', {
        method: 'POST',
        body: JSON.stringify({ ...registerForm, otp_code: otpForm.otp_code }),
      });
      applySession(session);
      setOtpForm(emptyOtpForm);
      setOtpDebugCode('');
      setAuthMessage('Workspace created. Complete MFA setup to secure your account.');
      navigate('/app/security');
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleResendRegisterOtp() {
    resetMessages();
    setAuthBusy(true);
    try {
      const response = await apiRequest('/api/v1/auth/register/send-otp', {
        method: 'POST',
        body: JSON.stringify({ email: registerForm.email }),
      });
      setOtpDebugCode(response.otp_code || '');
      setAuthMode('register-otp');
      setAuthMessage(response.message || 'Verification code sent to your email address.');
      return true;
    } catch (error) {
      setAuthError(error.message);
      return false;
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleLogin(event) {
    event.preventDefault();
    resetMessages();
    setAuthBusy(true);
    try {
      const session = await apiRequest('/api/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          ...loginForm,
          otp_code: otpChallengeRequired ? loginForm.otp_code || null : null,
          authenticator_code: mfaChallengeRequired ? loginForm.authenticator_code || null : null,
          recovery_code: mfaChallengeRequired ? loginForm.recovery_code || null : null,
        }),
      });
      if (session.mfa_required) {
        setMfaChallengeRequired(true);
        setOtpChallengeRequired(false);
        setShowRecoveryLoginInput(false);
        setAuthMessage(session.message || 'Authenticator code required. Enter your 6-digit code or a recovery code.');
      } else {
        applySession(session);
        setMfaChallengeRequired(false);
        setOtpChallengeRequired(false);
        setShowRecoveryLoginInput(false);
        setLoginForm((current) => ({ ...current, otp_code: '', authenticator_code: '', recovery_code: '' }));
        setLoginOtpDebugCode('');
        setAuthMessage('Signed in. Redirecting...');
      }
    } catch (error) {
      if (
        !mfaChallengeRequired &&
        !otpChallengeRequired &&
        (error.message || '').includes('Email verification code is required')
      ) {
        const sent = await handleSendLoginOtp();
        if (sent) {
          setOtpChallengeRequired(true);
          setAuthMessage('Email verification code sent. Enter the 6-digit code to continue.');
        }
        return;
      }
      setAuthError(error.message);
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleSendLoginOtp() {
    resetMessages();
    if (!loginForm.email) {
      setAuthError('Enter your work email first.');
      return false;
    }
    setAuthBusy(true);
    try {
      const response = await apiRequest('/api/v1/auth/login/send-otp', {
        method: 'POST',
        body: JSON.stringify({ email: loginForm.email }),
      });
      setLoginOtpDebugCode(response.otp_code || '');
      setOtpChallengeRequired(true);
      setMfaChallengeRequired(false);
      setAuthMessage(response.message || 'Verification code sent.');
      return true;
    } catch (error) {
      setAuthError(error.message);
      return false;
    } finally {
      setAuthBusy(false);
    }
  }

  async function handlePasswordResetRequest(event) {
    event.preventDefault();
    resetMessages();
    setAuthBusy(true);
    try {
      const response = await apiRequest('/api/v1/auth/password-reset/request', {
        method: 'POST',
        body: JSON.stringify(resetRequestForm),
      });
      setAuthMessage(
        response.reset_token
          ? `${response.message} Token: ${response.reset_token}`
          : response.message
      );
      if (response.reset_token) {
        setResetConfirmForm((current) => ({ ...current, token: response.reset_token }));
        setAuthMode('reset-confirm');
      }
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthBusy(false);
    }
  }

  async function handlePasswordResetConfirm(event) {
    event.preventDefault();
    resetMessages();
    setAuthBusy(true);
    try {
      const response = await apiRequest('/api/v1/auth/password-reset/confirm', {
        method: 'POST',
        body: JSON.stringify(resetConfirmForm),
      });
      setAuthMessage(response.message);
      setAuthMode('login');
      setResetConfirmForm(emptyResetConfirmForm);
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleAcceptInvitationFromLogin(event) {
    event.preventDefault();
    resetMessages();
    setAuthBusy(true);
    try {
      const session = await apiRequest('/api/v1/cloud-accounts/invitations/accept-public', {
        method: 'POST',
        body: JSON.stringify(loginInviteForm),
      });
      applySession(session);
      setLoginInviteForm(emptyLoginInviteForm);
      setAuthMessage(session.message || 'Invitation accepted. Redirecting...');
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleSendInvitationJoinOtp() {
    resetMessages();
    if (!loginInviteForm.token.trim()) {
      setAuthError('Enter invitation token first.');
      return false;
    }
    setAuthBusy(true);
    try {
      const response = await apiRequest('/api/v1/cloud-accounts/invitations/send-otp-public', {
        method: 'POST',
        body: JSON.stringify({ token: loginInviteForm.token.trim() }),
      });
      setAuthMessage(response.otp_code ? `${response.message} Code: ${response.otp_code}` : response.message);
      return true;
    } catch (error) {
      setAuthError(error.message);
      return false;
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleMfaSetup() {
    resetMessages();
    setAuthBusy(true);
    try {
      const response = await apiRequest('/api/v1/auth/mfa/setup', { method: 'POST' }, authToken);
      setMfaSetup(response);
      setRecoveryCodes([]);
      const posture = await apiRequest('/api/v1/auth/security-posture', {}, authToken);
      setSecurityPosture(posture);
      setAuthMessage(
        response.reused
          ? 'Existing authenticator setup loaded. Use your 6-digit code to enable MFA.'
          : 'Authenticator seed issued. Add it to your authenticator app, then confirm with a code.'
      );
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleMfaEnable(event) {
    event.preventDefault();
    resetMessages();
    setAuthBusy(true);
    try {
      const response = await apiRequest(
        '/api/v1/auth/mfa/enable',
        { method: 'POST', body: JSON.stringify({ authenticator_code: mfaEnableCode }) },
        authToken
      );
      const issuedCodes = response.recovery_codes || [];
      setRecoveryCodes(issuedCodes);
      setMfaEnableCode('');
      setMfaSetup(null);
      const posture = await apiRequest('/api/v1/auth/security-posture', {}, authToken);
      setSecurityPosture(posture);
      const user = await apiRequest('/api/v1/auth/me', {}, authToken);
      setCurrentUser(user);
      setAuthMessage(
        issuedCodes.length > 0
          ? 'Authenticator MFA enabled. Save the recovery codes shown below.'
          : 'Authenticator MFA enabled. Recovery codes were not returned. Use regenerate recovery codes below.'
      );
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleRecoveryCodesRegenerate(event) {
    event.preventDefault();
    resetMessages();
    setAuthBusy(true);
    try {
      const response = await apiRequest(
        '/api/v1/auth/mfa/recovery-codes/regenerate',
        {
          method: 'POST',
          body: JSON.stringify({
            authenticator_code: mfaDisableForm.authenticator_code || null,
            recovery_code: mfaDisableForm.recovery_code || null,
          }),
        },
        authToken
      );
      const newCodes = response.recovery_codes || [];
      setRecoveryCodes(newCodes);
      setAuthMessage(
        newCodes.length > 0
          ? `Recovery codes regenerated. Save this new set now (${newCodes.length} codes).`
          : 'Recovery code regeneration completed, but no codes were returned.'
      );
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleMfaDisable(event) {
    event.preventDefault();
    resetMessages();
    setAuthBusy(true);
    try {
      const response = await apiRequest(
        '/api/v1/auth/mfa/disable',
        {
          method: 'POST',
          body: JSON.stringify({
            authenticator_code: mfaDisableForm.authenticator_code || null,
            recovery_code: mfaDisableForm.recovery_code || null,
          }),
        },
        authToken
      );
      setMfaSetup(null);
      setRecoveryCodes([]);
      setMfaDisableForm({ authenticator_code: '', recovery_code: '' });
      const posture = await apiRequest('/api/v1/auth/security-posture', {}, authToken);
      setSecurityPosture(posture);
      const user = await apiRequest('/api/v1/auth/me', {}, authToken);
      setCurrentUser(user);
      setAuthMessage(response.message);
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthBusy(false);
    }
  }

  function handleLogout() {
    setAuthToken('');
    setCurrentUser(null);
    setCurrentTenant(null);
    setSecurityPosture(null);
    setDashboardOverview(null);
    setWeeklySummary(null);
    setWeeklySummaryWeek('');
    setAgingQueue(null);
    setBudgetRisk(null);
    setAllocationConfidence(null);
    setMfaSetup(null);
    setRecoveryCodes([]);
    setAuditEvents([]);
    setAuditError('');
    setCloudAccounts([]);
    setRecommendations([]);
    setTenantUsers([]);
    setCloudAccountRoleById({});
    setCloudSyncError('');
    setCloudSyncMessage('');
    setMfaChallengeRequired(false);
    setAuthMode('login');
    resetMessages();
    navigate('/');
  }

  async function refreshWorkspaceAnalytics() {
    if (!authToken) return;
    const [overview, summary, aging, risk, allocation, accounts, accountAccess, recs] = await Promise.all([
      apiRequest('/api/v1/dashboard/overview', {}, authToken),
      apiRequest(weeklySummaryPath(weeklySummaryWeek), {}, authToken),
      apiRequest('/api/v1/dashboard/aging-queue', {}, authToken),
      apiRequest('/api/v1/finops/budget-risk', {}, authToken),
      apiRequest('/api/v1/finops/allocation-confidence', {}, authToken),
      apiRequest('/api/v1/cloud-accounts', {}, authToken),
      apiRequest('/api/v1/cloud-accounts/my-access', {}, authToken),
      apiRequest('/api/v1/recommendations', {}, authToken),
    ]);
    setDashboardOverview(overview);
    setWeeklySummary(summary);
    setWeeklySummaryWeek(summary?.week || '');
    setAgingQueue(aging);
    setBudgetRisk(risk);
    setAllocationConfidence(allocation);
    setCloudAccounts(accounts || []);
    setRecommendations(recs || []);
    setCloudAccountRoleById(
      (accountAccess || []).reduce((acc, item) => {
        acc[item.cloud_account_id] = item.role;
        return acc;
      }, {})
    );
  }

  async function handleUpdateRecommendationStatus(recommendationId, statusValue) {
    await apiRequest(
      `/api/v1/recommendations/${encodeURIComponent(recommendationId)}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify({ status: statusValue }),
      },
      authToken,
    );
    await refreshWorkspaceAnalytics();
  }

  async function handleAssignRecommendation(recommendationId, assignedToUserId) {
    await apiRequest(
      `/api/v1/recommendations/${encodeURIComponent(recommendationId)}/assign`,
      {
        method: 'PATCH',
        body: JSON.stringify({ assigned_to_user_id: assignedToUserId || null }),
      },
      authToken,
    );
    await refreshWorkspaceAnalytics();
  }

  async function handleDashboardAction(recommendationId, newStatus) {
    await apiRequest(
      `/api/v1/dashboard/actions/${encodeURIComponent(recommendationId)}/execute`,
      { method: 'POST', body: JSON.stringify({ new_status: newStatus }) },
      authToken,
    );
    await refreshWorkspaceAnalytics();
  }

  async function loadCloudBootstrap(provider, accountId = '') {
    if (!authToken) return null;
    setCloudBootstrapBusy(true);
    setCloudBootstrapError('');
    try {
      const params = new URLSearchParams({ provider });
      if (accountId?.trim()) {
        params.set('account_id', accountId.trim());
      }
      const data = await apiRequest(`/api/v1/cloud-accounts/bootstrap?${params.toString()}`, {}, authToken);
      setCloudBootstrap(data);
      return data;
    } catch (error) {
      setCloudBootstrap(null);
      setCloudBootstrapError(error.message);
      return null;
    } finally {
      setCloudBootstrapBusy(false);
    }
  }

  async function handleCloudAccountCreate(event) {
    event.preventDefault();
    setCloudSyncBusy(true);
    setCloudSyncError('');
    setCloudSyncMessage('');
    try {
      await apiRequest('/api/v1/cloud-accounts', {
        method: 'POST',
        body: JSON.stringify(cloudAccountForm),
      }, authToken);
      setCloudAccountForm({ provider: 'aws', account_id: '', role_arn: '', external_id: '' });
      setCloudBootstrap(null);
      await refreshWorkspaceAnalytics();
      setCloudSyncMessage('Cloud account added successfully.');
      return true;
    } catch (error) {
      setCloudSyncError(error.message);
      return false;
    } finally {
      setCloudSyncBusy(false);
    }
  }

  async function handleSyncAllAccounts() {
    const accountIds = (cloudAccounts || []).map((account) => account.id);
    if (accountIds.length === 0) return;

    setCloudSyncAllBusy(true);
    setCloudSyncByAccount((prev) => {
      const next = { ...prev };
      accountIds.forEach((id) => { next[id] = true; });
      return next;
    });
    setCloudSyncError('');
    setCloudSyncMessage('Sync started — running in the background.');
    try {
      const response = await apiRequest('/api/v1/cloud-accounts/sync-all', { method: 'POST' }, authToken);
      setActiveSyncJobs((prev) => ({ ...prev, [response.job_id]: { scope: 'all', startedAt: Date.now() } }));
    } catch (error) {
      if (handleInvalidTokenError(error)) return;
      setCloudSyncError(error.message);
      setCloudSyncAllBusy(false);
      setCloudSyncByAccount((prev) => {
        const next = { ...prev };
        accountIds.forEach((id) => { delete next[id]; });
        return next;
      });
    }
  }

  async function handleSyncOneAccount(cloudAccountId) {
    if (cloudSyncByAccount[cloudAccountId]) return;
    setCloudSyncByAccount((prev) => ({ ...prev, [cloudAccountId]: true }));
    setCloudSyncError('');
    setCloudSyncMessage('Sync started — running in the background.');
    try {
      const response = await apiRequest(`/api/v1/cloud-accounts/${cloudAccountId}/sync`, { method: 'POST' }, authToken);
      setActiveSyncJobs((prev) => ({ ...prev, [response.job_id]: { scope: cloudAccountId, startedAt: Date.now() } }));
    } catch (error) {
      if (handleInvalidTokenError(error)) return;
      setCloudSyncError(error.message);
      setCloudSyncByAccount((prev) => { const next = { ...prev }; delete next[cloudAccountId]; return next; });
    }
  }

  async function handleUpdateAccount(cloudAccountId, roleArn, externalId) {
    setCloudSyncBusy(true);
    setCloudSyncError('');
    setCloudSyncMessage('');
    try {
      await apiRequest(`/api/v1/cloud-accounts/${cloudAccountId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role_arn: roleArn || null, external_id: externalId || null }),
      }, authToken);
      await refreshWorkspaceAnalytics();
      setCloudSyncMessage('Credentials updated.');
    } catch (error) {
      setCloudSyncError(error.message);
    } finally {
      setCloudSyncBusy(false);
    }
  }

  async function handleDeleteAccount(cloudAccountId) {
    setCloudSyncBusy(true);
    setCloudSyncError('');
    setCloudSyncMessage('');
    try {
      await apiRequest(`/api/v1/cloud-accounts/${cloudAccountId}`, {
        method: 'DELETE',
      }, authToken);
      await refreshWorkspaceAnalytics();
      setCloudSyncMessage('Account removed.');
    } catch (error) {
      setCloudSyncError(error.message);
    } finally {
      setCloudSyncBusy(false);
    }
  }

  async function handleBulkImport(file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch('/api/v1/cloud-accounts/bulk-import', {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}` },
      body: formData,
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.detail || 'Bulk import failed.');
    }
    await refreshWorkspaceAnalytics();
    return data;
  }

  async function handleOidcStart() {
    resetMessages();
    try {
      const response = await apiRequest('/api/v1/auth/oidc/authorize-url');
      window.open(response.authorize_url, '_blank', 'noopener,noreferrer');
      setAuthMessage('OIDC authorization opened in a new tab. Paste the returned code below to complete sign-in.');
    } catch (error) {
      setAuthError(error.message);
    }
  }

  async function handleOidcCodeExchange(event) {
    event.preventDefault();
    resetMessages();
    setAuthBusy(true);
    try {
      const session = await apiRequest('/api/v1/auth/oidc/token-exchange', {
        method: 'POST',
        body: JSON.stringify({ code: oidcCode }),
      });
      if (session.linked && session.access_token) {
        applySession(session);
      }
      setAuthMessage(session.message);
      setOidcCode('');
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthBusy(false);
    }
  }

  const ctx = {
    authToken, setAuthToken,
    authReady,
    authMode, setAuthMode,
    loginForm, setLoginForm,
    registerForm, setRegisterForm,
    resetRequestForm, setResetRequestForm,
    resetConfirmForm, setResetConfirmForm,
    loginInviteForm, setLoginInviteForm,
    health,
    catalog, catalogState,
    currentUser, currentTenant,
    securityPosture,
    authMessage, setAuthMessage,
    authError, setAuthError,
    authBusy,
    mfaSetup,
    mfaEnableCode, setMfaEnableCode,
    mfaDisableForm, setMfaDisableForm,
    recoveryCodes,
    dashboardPublic, dashboardOverview, weeklySummary, weeklySummaryWeek,
    agingQueue, budgetRisk, allocationConfidence,
    auditEvents, auditError,
    oidcProvider, oidcCode, setOidcCode,
    cloudAccounts, cloudAccountForm, setCloudAccountForm,
    cloudAccountRoleById,
    recommendations,
    tenantUsers,
    cloudBootstrap, cloudBootstrapBusy, cloudBootstrapError,
    cloudSyncBusy, cloudSyncAllBusy, cloudSyncByAccount, cloudSyncLastByAccount, cloudSyncMessage, cloudSyncError,
    activeSyncJobs,
    otpChallengeRequired, setOtpChallengeRequired,
    showRecoveryLoginInput, setShowRecoveryLoginInput,
    mfaChallengeRequired, setMfaChallengeRequired,
    otpForm, setOtpForm,
    otpDebugCode,
    loginOtpDebugCode,
    selectedPlan,
    trendPoints, riskItems, categoryItems, hasAnyAnalytics,
    hasPublicData,
    kpiSavings, kpiOpen, kpiAccepted, kpiSpend, kpiImplemented,
    accountBreakdown, topActions,
    resetMessages,
    handleRegister,
    handleCompleteRegister,
    handleResendRegisterOtp,
    handleLogin,
    handleSendLoginOtp,
    handlePasswordResetRequest,
    handlePasswordResetConfirm,
    handleAcceptInvitationFromLogin,
    handleSendInvitationJoinOtp,
    handleMfaSetup,
    handleMfaEnable,
    handleMfaDisable,
    handleRecoveryCodesRegenerate,
    handleLogout,
    loadCloudBootstrap,
    handleCloudAccountCreate,
    handleSyncAllAccounts,
    handleSyncOneAccount,
    handleUpdateAccount,
    handleDeleteAccount,
    handleBulkImport,
    handleOidcStart,
    handleOidcCodeExchange,
    handleDashboardAction,
    handleUpdateRecommendationStatus,
    handleAssignRecommendation,
    loadWeeklySummary,
  };

  return (
    <AppContext.Provider value={ctx}>
      <Routes>
        <Route path="/" element={<MarketingPage />} />
        <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/liability-disclaimer" element={<LiabilityDisclaimerPage />} />
        <Route path="/api-reference" element={<DocsPage />} />
        <Route path="/app" element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="accounts" element={<AccountsPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="recommendations" element={<RecommendationsPage />} />
          <Route path="assistant" element={<AssistantPage />} />
          <Route path="security" element={<SecurityPage />} />
          <Route path="website-inbox" element={<WebsiteInboxPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppContext.Provider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppWithRouter />
    </BrowserRouter>
  );
}

export default App;
